import React, { useEffect, useCallback } from 'react'
import { createContext, useContextSelector } from 'use-context-selector'
import { isEmpty, postSubmit } from 'ui-seeker/utils'

import { useAppContextState, useAppHashCompare } from 'app/@hooks'
import { COMPETITION_URL } from 'app/@common/constants'
import { CompetitionAnalyzerSubmittedFiltersContext } from '../../../Providers/@contexts'


const Context = createContext({})

const initialState = {
    currentDate: null,             // YYYYMMDD int — fecha de extracción más reciente
    availableDiffDays: [],         // [{target, tolerance, actual, date, enabled}]
    selected: null,                // target activo (3|7|15|30) o null si toggle apagado
    previousData: null,            // {id_hotel: {date_from: previous_item}} o null
    isLoadingDiscovery: false,     // primera llamada sin diff_days
    isLoadingPrevious: false,      // llamada con diff_days
}

/**
 * Contexto único por dashboard que gestiona el toggle "vs Previous Price".
 *
 *  - Eager discovery: al montar (y cada vez que cambian los submitted filters) hace una
 *    llamada a /previous-data SIN diff_days para descubrir los chips disponibles.
 *  - On-demand previous: cuando el usuario clica un chip se llama otra vez CON diff_days
 *    y se guarda `previousData` en el contexto.
 *  - Apagado por defecto: tras cambiar filtros, selected vuelve a null y previousData se vacía.
 *
 * Decisiones cerradas en PLAN-dashboards-previous-price-toggle.md §3.
 */
export function CompetitionAnalyzerVsPreviousPriceProvider({ children }) {
    const { state, updateState, replaceState } = useAppContextState(initialState)
    const submittedFilters = useContextSelector(CompetitionAnalyzerSubmittedFiltersContext, ({ state }) => state)
    const filtersChanged = useAppHashCompare(submittedFilters)

    // Discovery: filters cambian → reset estado y descubrir chips disponibles.
    useEffect(() => {
        if (isEmpty(submittedFilters) || !submittedFilters.cachedQuery) return

        replaceState({ ...initialState, isLoadingDiscovery: true })

        postSubmit(COMPETITION_URL.PREVIOUS_DATA, submittedFilters)
            .then(res => {
                updateState({
                    currentDate: res?.current_date ?? null,
                    availableDiffDays: res?.available_diff_days || [],
                    isLoadingDiscovery: false,
                })
            })
            .catch(() => updateState({ isLoadingDiscovery: false }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersChanged])

    // onSelect → null apaga toggle (sin llamada); número dispara fetch con diff_days.
    // `selected` se guarda como objeto {target, actual, date} en lugar de integer para que
    // consumidores aguas abajo (cards, deltas) puedan mostrar la fecha real del chip activo.
    const onSelect = useCallback(target => {
        if (target === null) {
            updateState({ selected: null, previousData: null })
            return
        }
        if (isEmpty(submittedFilters)) return

        // Pre-fill con la entrada conocida del chip (si está en availableDiffDays) — así
        // el chip se marca activo de inmediato con su date aunque la API tarde.
        const known = (state.availableDiffDays || []).find(e => e.target === target && e.enabled)
        const provisional = known
            ? { target: known.target, actual: known.actual, date: known.date }
            : { target, actual: null, date: null }
        updateState({ selected: provisional, isLoadingPrevious: true })

        postSubmit(COMPETITION_URL.PREVIOUS_DATA, { ...submittedFilters, diff_days: target })
            .then(res => {
                updateState({
                    previousData: res?.previous_data || null,
                    // El backend puede recalcular available_diff_days; lo refrescamos por si acaso.
                    availableDiffDays: res?.available_diff_days || state.availableDiffDays,
                    currentDate: res?.current_date ?? state.currentDate,
                    selected: res?.selected || provisional,
                    isLoadingPrevious: false,
                })
            })
            .catch(() => updateState({ isLoadingPrevious: false, previousData: null }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submittedFilters])

    return <Context.Provider value={{ state, updateState, onSelect }}>
        {children}
    </Context.Provider>
}

export default Context
